import type React from "react";
import { useEffect, useState } from "react";
import { Box, Typography, Button, CircularProgress } from "@mui/material";
import { Add as AddIcon } from "@mui/icons-material";
import { useSelector, useDispatch } from "react-redux";
import type { RootState, AppDispatch } from "../store";
import { fetchOperationsTeams } from "../store/features/operationsTeamSlice";
import { fetchUsers } from "../store/features/userSlice";
import { createOperationsTeamApi, updateOperationsTeamApi } from "../api/operationsTeamsApi";
import OperationsTeamTable from "../components/operationsTeams/OperationsTeamTable";
import OperationsTeamForm from "../components/operationsTeams/OperationsTeamForm";
import { useTranslation } from "react-i18next";
import type translations from "../translations/translations.json";
import { toastEmitter } from "../utils/toastEmitter";
import { getErrorMessage } from "../utils/errorHandling";
import { isUserAdmin } from "../types/user";
import { TestId } from "../constants/testIds";
import type { OperationsTeam } from "../types/operationsTeam";

type TranslationKeys = keyof typeof translations.en;

const OperationsTeamsPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { user, users } = useSelector((state: RootState) => state.user);
  const { operationsTeams, loading, error } = useSelector(
    (state: RootState) => state.operationsTeams,
  );
  const [open, setOpen] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [editingTeam, setEditingTeam] = useState<OperationsTeam | null>(null);
  const { t } = useTranslation();

  const isAdmin = isUserAdmin(user);

  useEffect(() => {
    dispatch(fetchOperationsTeams());
    dispatch(fetchUsers());
  }, [dispatch]);

  const handleOpen = () => {
    setEditingTeam(null);
    setOpen(true);
    setFormError(null);
  };

  const handleEdit = (team: OperationsTeam) => {
    setEditingTeam(team);
    setOpen(true);
    setFormError(null);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingTeam(null);
    setFormError(null);
  };

  const handleSubmit = async (data: { name: string; userIds: string[] }) => {
    try {
      if (editingTeam) {
        await updateOperationsTeamApi(editingTeam._id, data);
        toastEmitter.emit(t("Team updated successfully" as TranslationKeys), "success");
      } else {
        await createOperationsTeamApi(data);
        toastEmitter.emit(t("Team created successfully" as TranslationKeys), "success");
      }
      await dispatch(fetchOperationsTeams()).unwrap();
      handleClose();
    } catch (err: any) {
      const errorMessage = getErrorMessage(
        err,
        t,
        editingTeam ? "updateOperationsTeam" : "createOperationsTeam",
      );
      setFormError(errorMessage);
      toastEmitter.emit(errorMessage, "error");
    }
  };

  if (loading) {
    return (
      <Box
        sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ mt: 4 }}>
        <Typography color="error" sx={{ mt: 3 }}>
          {error}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h4" component="h1">
          {t("operationsTeams.title" as TranslationKeys)}
        </Typography>
        {isAdmin && (
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={handleOpen}
            data-testid={TestId.OPS_TEAM.ADD_BUTTON}
          >
            {t("Add Team" as TranslationKeys)}
          </Button>
        )}
      </Box>

      <OperationsTeamTable
        teams={operationsTeams}
        users={users}
        canEdit={isAdmin}
        onEdit={handleEdit}
      />

      <OperationsTeamForm
        open={open}
        onClose={handleClose}
        onSubmit={handleSubmit}
        error={formError}
        users={users}
        initialTeam={editingTeam}
      />
    </Box>
  );
};

export default OperationsTeamsPage;
